// ── Daily brief — the app's once-a-day welcome sheet ────────────────────────
//
// Pure helpers behind components/daily-brief/daily-brief-sheet.tsx: the
// once-per-IST-day memory, the fetch (which fails SILENTLY — see below), the
// "is another first-run gate on screen?" check, and the greeting.
//
// Failure rule (owner, 2026-09-26): if GET /daily-brief cannot be fetched —
// network error, timeout, non-2xx, wrong shape, or a stale day — the app shows
// NOTHING. No toast, no spinner, no fallback card, and the day is NOT marked as
// shown, so a later launch that day can still show it once the API recovers.
// The curated quote bank lives server-side; a healthy API always has a quote.

import type { AxiosRequestConfig } from "axios";
import { z } from "zod";
import { apiClient } from "@/api/client";
import { istYmd } from "@/lib/ist-time";
import i18n, { intlLocaleFor } from "@/i18n";

export const DAILY_BRIEF_TIMEOUT_MS = 4_000;

const Highlight = z.object({
  kind: z.string(),
  title: z.string().min(1),
  detail: z.string().nullable(),
  href: z.string().nullable(),
});

export const DailyBriefSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  role: z.string(),
  quote: z.object({ text: z.string().min(1), author: z.string().nullable() }),
  message: z.string().nullable(),
  source: z.enum(["ai", "curated"]).optional(),
  highlights: z.array(Highlight),
});

export type DailyBrief = z.infer<typeof DailyBriefSchema>;
export type DailyBriefHighlight = z.infer<typeof Highlight>;

/**
 * Roles that get the brief. A kiosk is a shared device; a driver's app is /drive.
 *
 * CHAIRMAN and DIRECTOR are OUT (owner, 2026-09-28: "a quote, all that should
 * not come"). An overseer opens the phone to see which of their schools is
 * slipping; a full-screen "Thought for the day" sheet stood between them and
 * that answer every morning, and its highlights only restated the network home
 * underneath it.
 */
const BRIEF_ROLES = new Set(["TEACHER", "STUDENT", "PARENT", "STAFF", "ADMIN", "PRINCIPAL"]);

export function roleGetsDailyBrief(role: string | null | undefined): boolean {
  return !!role && BRIEF_ROLES.has(role);
}

// ── once per IST day, per user ──────────────────────────────────────────────

/** One key per user holding the last IST day the brief was SHOWN. */
const SEEN_KEY = "ch-daily-brief-seen";

/**
 * `childId` scopes the "seen" memory for parents with more than one child:
 * the brief is per-child content (fees/homework/attendance belong to one
 * kid), so switching the active child must be able to show a fresh brief the
 * same day even if the previous child's brief was already dismissed.
 */
function seenKey(userId: string, childId?: string | null): string {
  return childId ? `${SEEN_KEY}:${userId}:${childId}` : `${SEEN_KEY}:${userId}`;
}

export function hasSeenDailyBrief(
  userId: string,
  day: string = istYmd(new Date()),
  childId?: string | null,
): boolean {
  if (!userId) return true;
  try {
    return localStorage.getItem(seenKey(userId, childId)) === day;
  } catch {
    // Storage blocked: we could never remember a showing, so never show —
    // a brief that reappears on every launch is a nag.
    return true;
  }
}

export function markDailyBriefSeen(
  userId: string,
  day: string = istYmd(new Date()),
  childId?: string | null,
): void {
  if (!userId) return;
  try {
    localStorage.setItem(seenKey(userId, childId), day);
  } catch {
    /* private mode — hasSeenDailyBrief already refuses to show without storage */
  }
}

/** Support hatch + test hook. */
export function forgetDailyBrief(userId: string, childId?: string | null): void {
  try {
    localStorage.removeItem(seenKey(userId, childId));
  } catch {
    /* ignore */
  }
}

// ── the fetch ───────────────────────────────────────────────────────────────

/**
 * Today's brief, or `null` on ANY failure. Goes straight to the API (not
 * `apiGet`): the offline cache would happily hand back yesterday's brief, and
 * the error interceptor would toast a failure the owner wants invisible.
 */
export async function fetchDailyBrief(opts: {
  aiConsented: boolean;
  timeoutMs?: number;
  today?: string;
  /** Parent's currently active child (useActiveChild().activeChildId). Omit for non-parent roles. */
  childId?: string | null;
}): Promise<DailyBrief | null> {
  const today = opts.today ?? istYmd(new Date());
  try {
    // `suppressErrorToast` is read by the client's interceptor (api/client.ts);
    // it is not part of axios' own config type, hence the named object.
    const config: AxiosRequestConfig & { suppressErrorToast: boolean } = {
      params: {
        ai: opts.aiConsented ? "1" : "0",
        ...(opts.childId ? { childId: opts.childId } : {}),
      },
      timeout: opts.timeoutMs ?? DAILY_BRIEF_TIMEOUT_MS,
      suppressErrorToast: true,
    };
    const res = await apiClient.get<{ success?: boolean; data?: unknown }>("/daily-brief", config);
    if (res.status !== 200 || !res.data?.success) return null;
    const parsed = DailyBriefSchema.safeParse(res.data.data);
    if (!parsed.success || parsed.data.date !== today) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

// ── sequencing ──────────────────────────────────────────────────────────────

/**
 * Anything else that owns the screen right now: a modal dialog, a first-run
 * gate (forced notice, student-type fix), the release walkthrough, the
 * first-login widget picker. The brief's own node carries `data-daily-brief`
 * and is ignored, or it would wait for itself forever.
 */
export function anotherGateIsUp(doc: Document = document): boolean {
  const hits = doc.querySelectorAll(
    "[data-firstrun-gate],[data-ch-tour],[role='dialog'][aria-modal='true'],[role='alertdialog']",
  );
  for (const el of Array.from(hits)) {
    if (!el.closest("[data-daily-brief]")) return true;
  }
  return false;
}

// ── copy ────────────────────────────────────────────────────────────────────

/**
 * Returns an i18next KEY, not the final string — callers translate with
 * `t(greetingFor())`. Reuses the dashboard namespace's existing
 * greetingMorning/Afternoon/Evening strings (same English copy as the
 * dashboard greeting) rather than a second, parallel set of keys.
 */
export function greetingFor(now: Date = new Date()): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", hour12: false }).format(now),
  );
  if (hour < 12) return "dashboard:greetingMorning";
  if (hour < 17) return "dashboard:greetingAfternoon";
  return "dashboard:greetingEvening";
}

export function firstNameOf(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/**
 * "Saturday, 26 September" for an IST day key — "शनिवार, 26 सितंबर" once
 * Hindi is on. Locale follows the shared i18n instance's current language;
 * the timezone always stays Asia/Kolkata regardless.
 */
export function longDate(day: string): string {
  const locale = intlLocaleFor(i18n.language);
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(`${day}T12:00:00+05:30`));
}

export type BriefAudience = "teacher" | "student" | "parent" | "staff" | "leader";

export function briefAudience(role: string | null | undefined): BriefAudience {
  switch (role) {
    case "TEACHER":
      return "teacher";
    case "STUDENT":
      return "student";
    case "PARENT":
      return "parent";
    case "ADMIN":
    case "PRINCIPAL":
    case "CHAIRMAN":
    case "DIRECTOR":
      return "leader";
    default:
      return "staff";
  }
}

/** Header photo, cropped small (public/images/daily-brief/*.webp, each < 25KB). */
export function briefImage(role: string | null | undefined): string {
  return `/images/daily-brief/${briefAudience(role)}.webp`;
}

/**
 * What to say when there is nothing on the list — true, and per role.
 * Returns an i18next KEY under the `dailyBrief` namespace; callers
 * translate with `t(emptyLine(role))`.
 */
export function emptyLine(role: string | null | undefined): string {
  switch (briefAudience(role)) {
    case "teacher":
      return "dailyBrief:emptyTeacher";
    case "student":
      return "dailyBrief:emptyStudent";
    case "parent":
      return "dailyBrief:emptyParent";
    case "leader":
      return "dailyBrief:emptyLeader";
    default:
      return "dailyBrief:emptyDefault";
  }
}
